/**
 * Axios instance + mutator for orval-generated hooks.
 *
 * Centralises:
 *  - Base URL resolution (env-driven, falls back to prod VPS)
 *  - JWT injection via request interceptor (reads from localStorage)
 *  - Transparent 401 → refresh → retry (TD-R034)
 *  - Error normalisation: non-2xx responses raise FseApiError so
 *    TanStack Query routes them to its error path.
 */
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

const DEFAULT_BASE_URL = 'https://api.foodsecurityexchange.cloud';

const ACCESS_KEY = 'fse_access_token';
const REFRESH_KEY = 'fse_refresh_token';
const LOGIN_PATH = '/login';

function resolveBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_FSE_API_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  return DEFAULT_BASE_URL;
}

function readToken(key: string): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(key);
}

function writeTokens(access: string, refresh: string) {
  window.localStorage.setItem(ACCESS_KEY, access);
  window.localStorage.setItem(REFRESH_KEY, refresh);
}

function clearTokens() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(ACCESS_KEY);
  window.localStorage.removeItem(REFRESH_KEY);
}

/**
 * Decode the current access token's payload to extract role / sub / exp.
 * No signature verification — purely a client-side read for routing.
 * Returns null if there's no token, the format is malformed, or atob fails.
 *
 * The server's JwtAuthGuard is the actual authority — this is just so
 * the SPA can route by role (e.g., sysCollab → /kanban.html, others →
 * /dashboard) without an extra round-trip to /auth/me.
 */
export interface JwtClaims {
  sub: string;
  role: string;
  typ: 'access' | 'refresh';
  iat?: number;
  exp?: number;
}

export function readAccessTokenClaims(): JwtClaims | null {
  const token = readToken(ACCESS_KEY);
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = parts[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(window.atob(padded)) as JwtClaims;
  } catch {
    return null;
  }
}

export function readCurrentRole(): string | null {
  return readAccessTokenClaims()?.role ?? null;
}

function bounceToLogin() {
  if (typeof window === 'undefined') return;
  // Hard navigation rather than router push — router instance isn't reachable
  // from inside the axios interceptor without extra plumbing, and a hard
  // nav guarantees the entire React tree resets (no stale React Query cache
  // bleed-through from the previous session).
  if (window.location.pathname !== LOGIN_PATH) {
    window.location.assign(LOGIN_PATH);
  }
}

const instance = axios.create({
  baseURL: resolveBaseUrl(),
  timeout: 15_000,
  headers: { Accept: 'application/json' },
});

instance.interceptors.request.use((config) => {
  const token = readToken(ACCESS_KEY);
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return config;
});

// --------------------------------------------------------------------------
// TD-R034: transparent 401 → refresh → retry
//
// When a request returns 401 AND it wasn't the refresh call itself, fire a
// one-shot exchange against /auth/refresh, persist new tokens, retry the
// original request once. Concurrent failures share a single in-flight
// refresh so we don't fan out under load.
// --------------------------------------------------------------------------

interface RetryableConfig extends InternalAxiosRequestConfig {
  __fseRetried?: boolean;
}

let refreshInFlight: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const refreshToken = readToken(REFRESH_KEY);
  if (!refreshToken) throw new Error('No refresh token');

  // Use a bare axios instance — going through `instance` would re-trigger
  // this interceptor and infinite-loop.
  const res = await axios.post<{
    access_token: string;
    refresh_token: string;
  }>(
    `${resolveBaseUrl()}/auth/refresh`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' }, timeout: 10_000 },
  );
  writeTokens(res.data.access_token, res.data.refresh_token);
  return res.data.access_token;
}

instance.interceptors.response.use(
  (response) => response,
  async (err: AxiosError) => {
    const original = err.config as RetryableConfig | undefined;
    const isAuthEndpoint = original?.url?.startsWith('/auth/') === true;
    const is401 = err.response?.status === 401;

    if (!is401 || !original || original.__fseRetried || isAuthEndpoint) {
      return Promise.reject(err);
    }

    original.__fseRetried = true;

    try {
      refreshInFlight ??= performRefresh().finally(() => {
        refreshInFlight = null;
      });
      const newAccess = await refreshInFlight;

      original.headers = original.headers ?? {};
      (original.headers as Record<string, string>).Authorization = `Bearer ${newAccess}`;
      return instance.request(original);
    } catch (refreshErr) {
      // Refresh itself failed → session is dead. Clear tokens and route to login.
      clearTokens();
      bounceToLogin();
      return Promise.reject(refreshErr);
    }
  },
);

export class FseApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message: string) {
    super(message);
    this.status = status;
    this.body = body;
    this.name = 'FseApiError';
  }
}

/**
 * Mutator entry point consumed by orval-generated endpoint modules.
 *
 * Orval (axios mode) contract: `<T>(config: AxiosRequestConfig) => Promise<T>`
 * where T is the response BODY type (not a wrapper). We return `response.data`
 * directly. Endpoints whose OpenAPI schema doesn't declare a response body
 * will type T as something like `void` — query consumers should use
 * `.isSuccess` rather than reading `.data` for those.
 */
export async function fseAxios<T>(config: AxiosRequestConfig): Promise<T> {
  try {
    const response: AxiosResponse<T> = await instance.request<T>(config);
    return response.data;
  } catch (err) {
    const ax = err as AxiosError;
    if (ax.response) {
      const body = ax.response.data;
      const message =
        body && typeof body === 'object' && 'message' in body
          ? String((body as { message: unknown }).message)
          : `${ax.response.status} ${ax.response.statusText}`;
      throw new FseApiError(ax.response.status, body, message);
    }
    throw err;
  }
}

export default fseAxios;
